/**
 * Register Component
 *
 * Provides a registration form for new users to create an account.
 * Uses the authStore for state management and handles validation, loading, and error states.
 */

import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import './auth.css';

export function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { register, isLoading, error, clearError } = useAuthStore();
  const { t } = useTranslation('auth');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearError();
    setValidationError(null);

    // Client-side validation
    if (password !== passwordConfirmation) {
      setValidationError(t('passwordsMismatch'));
      return;
    }

    if (password.length < 6) {
      setValidationError(t('passwordTooShort'));
      return;
    }

    try {
      await register({
        email,
        password,
        password_confirmation: passwordConfirmation,
      });
      navigate('/onboarding');
    } catch (error) {
      // Error is already set in the store
      console.error('Registration failed:', error);
    }
  };

  const displayError = validationError || error;

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>{t('registerTitle')}</h2>
        <p className="auth-subtitle">{t('registerSubtitle')}</p>

        {displayError && (
          <div className="error-message" role="alert">
            {displayError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">{t('email')}</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder={t('emailPlaceholder')}
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('password')}</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              placeholder={t('passwordPlaceholder')}
              disabled={isLoading}
              minLength={6}
            />
            <small className="form-hint">{t('passwordHint')}</small>
          </div>

          <div className="form-group">
            <label htmlFor="password-confirmation">{t('confirmPassword')}</label>
            <input
              id="password-confirmation"
              type="password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              required
              autoComplete="new-password"
              placeholder={t('passwordPlaceholder')}
              disabled={isLoading}
              minLength={6}
            />
          </div>

          <button type="submit" className="btn-primary" disabled={isLoading}>
            {isLoading ? t('creatingAccount') : t('register')}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            {t('hasAccount')}{' '}
            <a href="/login" className="auth-link">
              {t('loginHere')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;
